<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import TeamMemberQuickAdd, { type TeamPerson, type TeamRoleOption } from '@/components/TeamMemberQuickAdd.vue';
import TeamMemberHandoffModal from '@/components/TeamMemberHandoffModal.vue';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { ModuleRecord, PlanTask, TeamVacancy } from '@/types/domain';
import { errorMessage, formatCell } from '@/utils/format';

const props = withDefaults(defineProps<{
  projectId?: string | number | null;
  projectManager?: string | null;
  members: ModuleRecord[];
  /** 公司通讯录，快捷添加选人用 */
  people?: TeamPerson[];
  /** 项目角色选项（审批流用到的 + 已在用的） */
  roles?: TeamRoleOption[];
  /** 只读账号不给增删入口；后端另按项目再拦一次 */
  writable?: boolean;
}>(), {
  projectId: null,
  projectManager: null,
  people: () => [],
  roles: () => [],
  writable: true,
});

const emit = defineEmits<{
  edit: [member: ModuleRecord];
  /** 增删过人，父级重新拉名单 */
  changed: [];
}>();

/**
 * 职能分组。
 *
 * 之前这里画的是一棵组织树，但**数据里没有从属关系**：
 * 项目 1 的 17 个成员，15 个的上级都指向同一个项目经理，2 个没有上级——
 * 真实结构就是平的一层。与其编一个不存在的层级，不如按职能归类——
 * 这是能从角色名真实推出来的。空岗也按同一套规则落进对应的组。
 */
const GROUPS: ReadonlyArray<{ key: string; label: string; match: RegExp }> = [
  /*
    「项目管理」这一组只放**对这个项目负责的人**，也就是项目经理。

    0907 Tony：秦岭OP3 的项目经理是何荣伟，肖娟是「项目主管」——她在团队里
    只是因为审批流第 2 级点名要这个角色。两张卡片并排摆在「项目管理」下面，
    看着就像这个项目有两个项目经理，谁说了算分不清。这类角色归「其他角色」。
    （「计划」归 PMC 也是同一类修正：计划物流部经理是物控口的，0904 Tony。）
  */
  { key: 'pm', label: '项目管理', match: /项目经理|项目管理/u },
  { key: 'engineering', label: '应用工程', match: /应用工程|工艺/u },
  { key: 'pmc', label: 'PMC / 物控', match: /PMC|物控|排产|计划|物流/u },
  { key: 'tooling', label: '模具 / 工装', match: /模具|工装/u },
  { key: 'quality', label: '质量', match: /质量|PQE|SQE|QC|检验/u },
  { key: 'commercial', label: '商务 / 采购', match: /客户|销售|采购|商务/u },
  { key: 'product', label: '产品 / 包装', match: /产品|包装|设计/u },
];
const OTHER = { key: 'other', label: '其他角色', match: /.^/u };

const TERMINAL = new Set(['completed', 'approved', 'cancelled', 'not_applicable']);

const tasks = ref<PlanTask[]>([]);
const loadingTasks = ref(false);

const vacancies = ref<TeamVacancy[]>([]);
const vacancyState = ref<'loading' | 'ready' | 'error'>('loading');
/** 参与统计的节点数：0 说明本项目还没挂审批流，「没有空岗」就不是好消息 */
const checkedNodes = ref(0);
/** 挂在停用账号上的成员 id：卡片上写「已关联账号」是误导，审批派不到、通知收不到 */
const inactiveIds = ref<Set<string>>(new Set());
const showVacancies = ref(true);

const query = ref('');
const removingId = ref<string | number | null>(null);
const handoff = ref<{ open: boolean; member: ModuleRecord | null }>({ open: false, member: null });
const activatingId = ref<string | number | null>(null);

const quickAdd = ref<{ open: boolean; presetRole: string; groupKey: string | null; replace: ModuleRecord | null }>({
  open: false, presetRole: '', groupKey: null, replace: null,
});

function memberName(member: ModuleRecord): string {
  const value = formatCell(member.title || member.name);
  return value === '—' ? '姓名未维护' : value;
}

function memberRole(member: ModuleRecord): string {
  const value = formatCell(member.code || member.roleTitle);
  return value === '—' ? '项目角色未维护' : value;
}

function accountBound(member: ModuleRecord): boolean {
  return Boolean(member.parent_id ?? member.parentId);
}

/**
 * 账号已停用 = 人已离职（Tony 2026-09-05 定的口径）。
 * 和没绑账号一样收不到通知、审批派不到；该做的事是换人，不是留着（秦岭OP3 李香纯）。
 */
function accountInactive(member: ModuleRecord): boolean {
  return inactiveIds.value.has(String(member.id));
}

/**
 * 成员记录本身没生效。
 *
 * 建项目时按模板铺出来的岗位是 status='vacant'（岗位有了、人还没定）。项目经理在
 * 这一页把人填进去时，改的是姓名和账号——**状态不跟着变**，而审批派人只认
 * status='active'。于是卡片上人好好地站着，审批却把他当不存在的人跳过去。
 * 2026-09-08 DH26003 就是这样：11 个岗位填了人全卡在 vacant，9 个角色派不到人，
 * 界面上一点痕迹都没有。后端已经改成「填了人自动生效」，这里把存量的标出来并给
 * 一键启用——不然人看不见，也没有别的入口能改。
 */
function needsActivation(member: ModuleRecord): boolean {
  const status = String(member.status ?? '').trim();
  return Boolean(status) && status !== 'active';
}

/** 这个人现在到底收不收得到通知、审批派不派得到 */
function reachable(member: ModuleRecord): boolean {
  return accountBound(member) && !accountInactive(member) && !needsActivation(member);
}

function userId(member: ModuleRecord): string {
  return String(member.parent_id ?? member.parentId ?? '');
}

function memberEmail(member: ModuleRecord): string {
  const value = String(member.owner_name ?? member.ownerName ?? '').trim();
  return value.includes('@') ? value : '';
}

/**
 * 头像上的字。
 *
 * 中文名取后两个字（姓在前，名更认得出人）；两个字的整个用；
 * 拉丁名取首字母。颜色按名字散列固定，同一个人在哪都是同一个色。
 */
function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  if (/^[一-龥·]+$/u.test(trimmed)) return trimmed.length <= 2 ? trimmed : trimmed.slice(-2);
  return trimmed.split(/\s+/u).map((part) => part[0]?.toUpperCase() ?? '').join('').slice(0, 2);
}

function hue(name: string): number {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.codePointAt(0)!) % 997;
  return hash % 6;
}

/**
 * 每个人手上压着多少。
 *
 * 优先按账号匹配；没绑账号的退回按姓名匹配——姓名可能重名，
 * 所以这种情况卡片上会标一个 ≈，让人知道数字未必准。
 */
const workload = computed(() => {
  const byUser = new Map<string, { total: number; overdue: number }>();
  const byName = new Map<string, { total: number; overdue: number }>();
  const bump = (map: Map<string, { total: number; overdue: number }>, key: string, late: boolean): void => {
    if (!key) return;
    const entry = map.get(key) ?? { total: 0, overdue: 0 };
    entry.total += 1;
    if (late) entry.overdue += 1;
    map.set(key, entry);
  };
  for (const task of tasks.value) {
    if (TERMINAL.has(task.status) || task.actualEnd) continue;
    bump(byUser, String(task.ownerUserId ?? ''), task.overdue);
    bump(byName, String(task.ownerName ?? '').trim(), task.overdue);
  }
  return { byUser, byName };
});

function loadOf(member: ModuleRecord): { total: number; overdue: number; exact: boolean } {
  const uid = userId(member);
  const exact = Boolean(uid && workload.value.byUser.has(uid));
  const entry = exact
    ? workload.value.byUser.get(uid)
    : workload.value.byName.get(memberName(member));
  return { total: entry?.total ?? 0, overdue: entry?.overdue ?? 0, exact };
}

function matchesQuery(text: string): boolean {
  const needle = query.value.trim().toLowerCase();
  return !needle || text.toLowerCase().includes(needle);
}

interface Group {
  key: string;
  label: string;
  match: RegExp;
  members: ModuleRecord[];
  vacancies: TeamVacancy[];
}

const grouped = computed<Group[]>(() => {
  const buckets: Group[] = [...GROUPS, OTHER].map((group) => ({ ...group, members: [], vacancies: [] }));
  const bucketOf = (role: string): Group =>
    buckets.find((group) => group.key !== 'other' && group.match.test(role)) ?? buckets[buckets.length - 1]!;
  for (const member of props.members) {
    const role = memberRole(member);
    if (!matchesQuery(`${memberName(member)} ${role}`)) continue;
    bucketOf(role).members.push(member);
  }
  if (showVacancies.value) {
    // 只有会卡住的进组做卡片；借人的在上面的标签条里
    for (const vacancy of blocked.value) {
      if (!matchesQuery(vacancy.role)) continue;
      bucketOf(vacancy.role).vacancies.push(vacancy);
    }
  }
  return buckets.filter((group) => group.members.length || group.vacancies.length);
});

const visibleCount = computed(() => grouped.value.reduce((sum, group) => sum + group.members.length, 0));
const roleCount = computed(() => new Set(props.members.map(memberRole)).size);
const unbound = computed(() => props.members.filter((member) => !accountBound(member)).length);
const inactiveCount = computed(() => props.members.filter(accountInactive).length);
/** 收不到通知的人 = 没绑账号 + 账号已停用 */
const unreachable = computed(() => unbound.value + inactiveCount.value);
const totalLoad = computed(() =>
  props.members.reduce((sum, member) => sum + loadOf(member).total, 0));
const overloaded = computed(() => props.members
  .map((member) => ({ member, load: loadOf(member) }))
  .filter((item) => item.load.overdue > 0)
  .sort((a, b) => b.load.overdue - a.load.overdue)
  .slice(0, 3));
/**
 * 三档分开摆（生产实测 SN26004：37 个点名角色 28 个没配）：
 * 会卡住的进职能组做虚线卡片；借人的收成一排标签；按岗位自动认到的只在脚注提一句。
 * 全铺成卡片的话，28 张虚线框会把 12 个真人淹掉。
 */
const blocked = computed(() => vacancies.value.filter((item) => item.resolvedFrom === 'none'));
const borrowed = computed(() => vacancies.value.filter((item) => item.resolvedFrom === 'company'));
const byPosition = computed(() => vacancies.value.filter((item) => item.resolvedFrom === 'position'));
/** 借人的标签默认只露前 8 个：实测有 24 个，全铺开比人还多 */
const BORROWED_PREVIEW = 8;
const allBorrowed = ref(false);
const shownBorrowed = computed(() =>
  (allBorrowed.value ? borrowed.value : borrowed.value.slice(0, BORROWED_PREVIEW)));
/** KPI 上的空岗数：会卡住的 + 借人的；按岗位认到的不算空岗 */
const vacancyCount = computed(() => blocked.value.length + borrowed.value.length);
/** 按岗位认到的角色要写出是谁：秦岭OP3 的「项目主管」认到的是肖娟，只写角色名她就从页面上消失了（0904 Tony） */
const positionNames = computed(() => {
  const names = byPosition.value.map((item) =>
    (item.borrowed[0] ? `${item.role} ${item.borrowed[0]}` : item.role));
  return names.slice(0, 8).join('、') + (names.length > 8 ? '…' : '');
});

/** 空岗那格 KPI 的说明文字：数字是 0 时要说清是「都配齐了」还是「没法算」 */
const vacancySummary = computed(() => {
  if (vacancyState.value === 'loading') return '统计中…';
  if (vacancyState.value === 'error') return '没算出来，刷新再试';
  if (!checkedNodes.value) return '本项目的任务还没挂审批流，无从判断';
  if (!vacancyCount.value) return '审批流点名的角色都有人担';
  const parts: string[] = [];
  if (blocked.value.length) parts.push(`${blocked.value.length} 个会卡住审批`);
  if (borrowed.value.length) parts.push(`${borrowed.value.length} 个借别的项目的人`);
  return parts.join(' · ');
});

async function loadTasks(): Promise<void> {
  if (!props.projectId) return;
  loadingTasks.value = true;
  try {
    const payload = await eqsApi.projectPlan(props.projectId);
    tasks.value = (payload?.tasks ?? []).filter((task) => task.nodeType !== 'phase');
  } catch {
    // 取不到负荷不影响看名单，静默降级
    tasks.value = [];
  } finally {
    loadingTasks.value = false;
  }
}

async function loadVacancies(): Promise<void> {
  if (!props.projectId) {
    vacancyState.value = 'ready';
    return;
  }
  vacancyState.value = 'loading';
  try {
    const payload = await eqsApi.teamVacancies(props.projectId);
    vacancies.value = payload?.vacancies ?? [];
    checkedNodes.value = payload?.checkedNodes ?? 0;
    inactiveIds.value = new Set((payload?.inactiveAccounts ?? []).map((item) => String(item.memberId)));
    vacancyState.value = 'ready';
  } catch {
    // 空岗算不出来不该让名单跟着消失；KPI 那格会说「没算出来」
    vacancies.value = [];
    vacancyState.value = 'error';
  }
}

watch(() => props.projectId, () => { void loadTasks(); void loadVacancies(); }, { immediate: true });
// 名单变了（加人、移出）空岗要跟着重算；负荷不变，不重取
watch(() => props.members, () => { void loadVacancies(); });

/**
 * 移出：卡片右上角直接点，确认一次就走（0904 Tony）。
 * 原来要先打开编辑弹窗 → 抽屉「成员操作」→「移出项目」→ 确认，四步。
 */
/** 把卡在 vacant / 其他非生效状态的成员记录转正，转完他才进得了审批链 */
async function activate(member: ModuleRecord): Promise<void> {
  const name = memberName(member);
  activatingId.value = member.id;
  try {
    const activated = await eqsApi.advanceProjectResource('hr', 'project-members', member.id, 'active');
    const assigned = Number((activated as { ownerSync?: { assigned?: number } }).ownerSync?.assigned ?? 0);
    notify('success', '已启用', `${name} · ${memberRole(member)} 现在能收到审批了`
      + (assigned > 0 ? `；按角色给 ${assigned} 个阶段任务落了负责人` : ''));
    emit('changed');
  } catch (caught) {
    notify('error', '启用失败', errorMessage(caught));
  } finally {
    activatingId.value = null;
  }
}

async function remove(member: ModuleRecord): Promise<void> {
  if (!props.projectId) {
    notify('error', '移出失败', '缺少项目编号');
    return;
  }
  handoff.value = { open: true, member };
}

function closeHandoff(): void {
  handoff.value = { open: false, member: null };
}
function onHandoffRemoved(): void {
  closeHandoff();
  emit('changed');
}

function openQuickAdd(options: { presetRole?: string; groupKey?: string | null; replace?: ModuleRecord | null } = {}): void {
  quickAdd.value = {
    open: true,
    presetRole: options.presetRole ?? '',
    groupKey: options.groupKey ?? null,
    replace: options.replace ?? null,
  };
}

/** 已离职的人：角色带上，选个新人，加入后把旧的那条移出 */
function replaceMember(member: ModuleRecord): void {
  openQuickAdd({ presetRole: memberRole(member), replace: member });
}

const quickAddGroup = computed(() => (quickAdd.value.groupKey
  ? GROUPS.find((group) => group.key === quickAdd.value.groupKey) ?? null
  : null));

defineExpose({ openQuickAdd });
</script>

<template>
  <section class="team-view" aria-labelledby="team-view-title">
    <header class="team-head">
      <div>
        <span class="team-kicker">TEAM / 项目团队</span>
        <h2 id="team-view-title">团队与分工</h2>
        <p>
          按职能分组。点卡片改角色和账号，卡片右上角可以直接移出。
          审批流点名但本项目没配的角色会用虚线卡片或标签标出来，点一下就能安排人。
        </p>
      </div>
      <div class="team-tools">
        <span class="team-count"><b>{{ members.length }}</b> 人 · {{ roleCount }} 种角色</span>
        <label class="team-search">
          <input v-model="query" type="search" placeholder="搜姓名或角色" aria-label="搜索团队成员">
        </label>
        <label v-if="vacancyCount" class="team-toggle">
          <input v-model="showVacancies" type="checkbox"> 显示空岗
        </label>
      </div>
    </header>

    <div class="team-kpis">
      <article>
        <span>项目经理</span>
        <b>{{ projectManager || '未指定' }}</b>
      </article>
      <article :class="{ warning: unreachable > 0 }">
        <span>收不到通知的人</span>
        <b>{{ unreachable }}</b>
        <small v-if="!unreachable">全员都能收到待办和审批通知</small>
        <small v-else>
          <template v-if="unbound">{{ unbound }} 个没关联账号</template>
          <template v-if="unbound && inactiveCount"> · </template>
          <template v-if="inactiveCount">{{ inactiveCount }} 个已离职，请换人</template>
        </small>
      </article>
      <article :class="{ risk: overloaded.length > 0 }">
        <span>名下未完成任务</span>
        <b>{{ loadingTasks ? '…' : totalLoad }}</b>
        <small v-if="overloaded.length" class="team-hotspot">
          逾期最多：<template v-for="(item, index) in overloaded" :key="item.member.id">
            <template v-if="index">、</template>{{ memberName(item.member) }} <em>{{ item.load.overdue }}</em>
          </template>
        </small>
        <small v-else-if="!loadingTasks">没有逾期</small>
      </article>
      <article :class="{ warning: borrowed.length > 0 && !blocked.length, risk: blocked.length > 0 }">
        <span>空岗</span>
        <b>{{ vacancyState === 'ready' ? vacancyCount : '…' }}</b>
        <small>{{ vacancySummary }}</small>
      </article>
    </div>

    <!-- 借人的角色：一排标签，不做卡片。实测 24 个，做卡片会比人还多 -->
    <section v-if="showVacancies && borrowed.length" class="team-borrowed" aria-label="借别的项目的人在审批的角色">
      <div class="team-borrowed-head">
        <b>审批流还点名了 {{ borrowed.length }} 个本项目没配的角色</b>
        <span>现在都是借别的项目里担这个角色的人在审批。点一个给本项目安排人。</span>
      </div>
      <div class="team-chips">
        <button
          v-for="vacancy in shownBorrowed"
          :key="`chip-${vacancy.role}`"
          type="button"
          class="team-chip"
          :disabled="!writable"
          :aria-label="`给「${vacancy.role}」安排人`"
          @click="openQuickAdd({ presetRole: vacancy.role })"
        >
          <b>＋ {{ vacancy.role }}</b>
          <small>{{ vacancy.nodeCount }} 个任务 · 借 {{ vacancy.borrowed[0] || '—' }}</small>
        </button>
        <button
          v-if="borrowed.length > BORROWED_PREVIEW"
          type="button"
          class="inline-button"
          @click="allBorrowed = !allBorrowed"
        >{{ allBorrowed ? '收起' : `展开全部 ${borrowed.length} 个` }}</button>
      </div>
    </section>
    <p v-if="showVacancies && byPosition.length" class="team-position-note">
      另有 {{ byPosition.length }} 个角色（{{ positionNames }}）按岗位自动找人，不用在项目里配。
    </p>

    <div v-if="grouped.length" class="team-groups">
      <section v-for="group in grouped" :key="group.key" class="team-group">
        <h3>
          {{ group.label }}
          <small>{{ group.members.length }}</small>
          <em v-if="group.vacancies.length">{{ group.vacancies.length }} 个空岗</em>
          <!-- 组级「加人」放标题旁：每组末尾再摆一张大卡片，7 组就是 7 张，比人还多 -->
          <button
            v-if="writable && !query.trim()"
            type="button"
            class="team-add"
            :aria-label="`往「${group.label}」里加人`"
            @click="openQuickAdd({ groupKey: group.key })"
          >＋ 加人</button>
        </h3>
        <div class="team-cards">
          <article
            v-for="member in group.members"
            :key="member.id"
            class="team-card"
            :class="{ unbound: !reachable(member), busy: loadOf(member).overdue > 0, removing: removingId === member.id }"
          >
            <button
              type="button"
              class="team-card-main"
              :aria-label="`编辑 ${memberName(member)} 的角色与账号`"
              @click="emit('edit', member)"
            >
              <span class="team-avatar" :class="`hue-${hue(memberName(member))}`">{{ initials(memberName(member)) }}</span>
              <span class="team-card-body">
                <b>{{ memberName(member) }}</b>
                <span class="team-role">{{ memberRole(member) }}</span>
                <span class="team-load">
                  <template v-if="loadingTasks">…</template>
                  <template v-else-if="loadOf(member).total">
                    {{ loadOf(member).total }} 项未完成
                    <em v-if="loadOf(member).overdue">{{ loadOf(member).overdue }} 逾期</em>
                    <i v-if="!loadOf(member).exact" title="按姓名匹配，重名时可能不准">≈</i>
                  </template>
                  <template v-else>名下无未完成任务</template>
                </span>
                <span class="team-account" :class="{ missing: !reachable(member) }">
                  <template v-if="accountInactive(member)">已离职 · 审批派不到他，请换人</template>
                  <template v-else-if="needsActivation(member)">未生效 · 审批派不到他，点「启用」</template>
                  <template v-else-if="accountBound(member)">
                    已关联账号<template v-if="memberEmail(member)"> · {{ memberEmail(member) }}</template>
                  </template>
                  <template v-else>未关联账号 · 收不到通知</template>
                </span>
              </span>
            </button>
            <div v-if="writable" class="team-card-actions">
              <button
                v-if="accountInactive(member)"
                type="button"
                class="team-action primary"
                :title="`${memberName(member)} 已离职，给「${memberRole(member)}」换一个人`"
                @click="replaceMember(member)"
              >换人</button>
              <button
                v-else-if="needsActivation(member)"
                type="button"
                class="team-action primary"
                :title="`${memberName(member)} 的成员记录还没生效，启用之后才会进审批链`"
                :disabled="activatingId !== null"
                @click="activate(member)"
              >{{ activatingId === member.id ? '启用中…' : '启用' }}</button>
              <button
                type="button"
                class="team-action"
                :title="`编辑 ${memberName(member)} 的角色与账号`"
                @click="emit('edit', member)"
              >编辑</button>
              <button
                type="button"
                class="team-action danger"
                :title="`把 ${memberName(member)} 移出项目`"
                :disabled="removingId !== null"
                @click="remove(member)"
              >{{ removingId === member.id ? '移出中…' : '移出' }}</button>
            </div>
          </article>

          <button
            v-for="vacancy in group.vacancies"
            :key="`vacancy-${vacancy.role}`"
            type="button"
            class="team-card team-vacancy"
            :class="{ blocked: vacancy.resolvedFrom === 'none' }"
            :disabled="!writable"
            :aria-label="`给「${vacancy.role}」安排人`"
            @click="openQuickAdd({ presetRole: vacancy.role, groupKey: group.key })"
          >
            <span class="team-avatar vacant">＋</span>
            <span class="team-card-body">
              <b>{{ vacancy.role }}</b>
              <span class="team-role">空岗 · {{ vacancy.nodeCount }} 个任务的审批要这个角色</span>
              <span v-if="vacancy.resolvedFrom === 'company'" class="team-vacancy-note">
                现在借 {{ vacancy.borrowed.join('、') }} 审批
              </span>
              <span v-else class="team-vacancy-note blocked">全公司找不到人，走到这级会卡住</span>
              <span v-if="writable" class="team-vacancy-cta">安排人 →</span>
            </span>
          </button>

        </div>
      </section>
    </div>

    <div v-else-if="query.trim()" class="team-empty static">
      <b>没有叫「{{ query.trim() }}」的人或角色</b>
      <span>本项目 {{ members.length }} 人里没有匹配的；清掉搜索框回到全部。</span>
    </div>

    <button v-else-if="writable" class="team-empty" type="button" @click="openQuickAdd()">
      <b>本项目还没有团队成员</b>
      <span>点这里加第一个人。审批、评审都按团队里的角色找人，先把审批流点名的角色配上。</span>
    </button>
    <div v-else class="team-empty static">
      <b>本项目还没有团队成员</b>
      <span>你的账号只有查看权限，请项目经理来维护。</span>
    </div>

    <p v-if="query.trim() && grouped.length" class="team-filter-note">
      按「{{ query.trim() }}」筛出 {{ visibleCount }} / {{ members.length }} 人。
    </p>

    <p class="team-note">
      名单只记「谁担什么角色」，没有多级汇报关系，所以不画组织树。
      审批和评审按角色名找人，角色要和审批流里的写法一致，否则派不到人。
    </p>

    <TeamMemberQuickAdd
      v-if="projectId"
      :open="quickAdd.open"
      :project-id="projectId"
      :people="people"
      :roles="roles"
      :members="members"
      :preset-role="quickAdd.presetRole || undefined"
      :group-label="quickAddGroup?.label"
      :group-match="quickAddGroup?.match ?? null"
      :replacing="quickAdd.replace
        ? { id: quickAdd.replace.id, name: memberName(quickAdd.replace), role: memberRole(quickAdd.replace) }
        : undefined"
      @close="quickAdd.open = false"
      @added="emit('changed')"
    />

    <TeamMemberHandoffModal
      v-if="handoff.member && projectId"
      :open="handoff.open"
      :project-id="projectId"
      :member="handoff.member"
      :people="people"
      :team-members="members"
      @close="closeHandoff"
      @removed="onHandoffRemoved"
    />
  </section>
</template>

<style scoped>
.team-view { display: grid; gap: 16px; margin: 16px 0; padding: 18px 20px 16px; border: 1px solid var(--line); border-radius: var(--radius); background: var(--white); box-shadow: 0 1px 2px rgba(13, 36, 66, .04); }

.team-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.team-kicker { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.team-head h2 { margin: 3px 0 4px; font-size: 20px; }
.team-head p { margin: 0; max-width: 64ch; color: var(--muted); font-size: 13px; line-height: 1.8; }
.team-tools { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.team-count { color: var(--muted); font-size: 12px; white-space: nowrap; }
.team-count b { margin-right: 2px; color: var(--ink); font: 700 22px var(--display); }
.team-search input { min-width: 180px; min-height: 34px; padding: 0 10px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); font: inherit; font-size: 13px; }
.team-search input:focus-visible { outline: 2px solid var(--blue); outline-offset: 1px; }
.team-toggle { display: inline-flex; align-items: center; gap: 5px; color: var(--muted); font-size: 12px; cursor: pointer; white-space: nowrap; }

.team-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; }
.team-kpis article { display: grid; gap: 2px; align-content: start; padding: 11px 13px; border: 1px solid var(--line); border-left: 3px solid var(--blue); border-radius: var(--radius-s); background: var(--paper); }
.team-kpis article.warning { border-left-color: var(--warning-graphic); }
.team-kpis article.risk { border-left-color: var(--risk-graphic); }
.team-kpis article > span { color: var(--muted); font-size: 12px; }
.team-kpis b { font: 700 22px var(--display); line-height: 1.2; }
.team-kpis article.warning b { color: var(--warning); }
.team-kpis article.risk b { color: var(--risk); }
.team-kpis small { color: var(--muted); font-size: 12px; line-height: 1.5; }
.team-hotspot em { color: var(--risk); font: 700 13px var(--display); font-style: normal; }

.team-groups { display: grid; gap: 18px; }
.team-group h3 { display: flex; align-items: baseline; gap: 8px; margin: 0 0 8px; font-size: 14px; }
.team-group h3 small { padding: 0 7px; border-radius: 9px; background: var(--paper-2); color: var(--muted); font-size: 12px; }
.team-group h3 em { color: var(--warning); font-size: 12px; font-style: normal; font-weight: 700; }
.team-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(232px, 1fr)); gap: 10px; }

/* 卡片：主体是一个大按钮（编辑），右上角另放两个小按钮——按钮不能套按钮 */
.team-card { position: relative; display: block; min-height: 96px; padding: 0; border: 1px solid var(--line); border-left: 3px solid var(--blue); border-radius: var(--radius-s); background: var(--white); text-align: left; transition: border-color .15s, box-shadow .15s, transform .15s; }
.team-card:hover, .team-card:focus-within { border-color: var(--blue); box-shadow: 0 6px 18px rgba(13, 36, 66, .1); }
.team-card.unbound { border-left-color: var(--warning-graphic); }
.team-card.busy { border-left-color: var(--risk-graphic); }
.team-card.removing { opacity: .55; }
.team-card-main { display: flex; gap: 10px; width: 100%; padding: 12px 12px 12px 11px; border: 0; background: transparent; color: inherit; text-align: left; cursor: pointer; }
.team-card-main:focus-visible { outline: 2px solid var(--blue); outline-offset: -2px; border-radius: var(--radius-s); }
.team-card-body { display: grid; gap: 3px; min-width: 0; flex: 1; }
.team-card-body b { font-size: 14px; line-height: 1.3; padding-right: 76px; }
.team-role { color: var(--ink); font-size: 12px; }
.team-load { color: var(--muted); font-size: 12px; }
.team-load em { margin-left: 5px; color: var(--risk); font-style: normal; font-weight: 700; }
.team-load i { margin-left: 3px; color: var(--muted); font-style: normal; cursor: help; }
.team-account { color: var(--green); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.team-account.missing { color: var(--warning); font-weight: 700; }

.team-avatar { flex: 0 0 38px; width: 38px; height: 38px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; font: 700 13px var(--display); letter-spacing: .02em; }
.team-avatar.hue-0 { background: #dbe8fb; color: #1d4f9c; }
.team-avatar.hue-1 { background: #d7f0ea; color: #1f6b58; }
.team-avatar.hue-2 { background: #fbe6cf; color: #8a4f0f; }
.team-avatar.hue-3 { background: #e7dff8; color: #4f3a91; }
.team-avatar.hue-4 { background: #fbdede; color: #9a3230; }
.team-avatar.hue-5 { background: #dcf0f8; color: #1a5f7a; }
.team-avatar.vacant, .team-avatar.plus { border: 1.5px dashed var(--line-strong); background: transparent; color: var(--blue); font-size: 18px; }

/* 右上角动作：一直可见但克制，悬停/聚焦时变成正色 */
.team-card-actions { position: absolute; top: 9px; right: 9px; display: flex; gap: 4px; }
.team-action { min-height: 24px; padding: 0 8px; border: 1px solid var(--line); border-radius: 6px; background: var(--white); color: var(--muted); font: 700 11px var(--body); line-height: 1; cursor: pointer; transition: border-color .15s, color .15s, background-color .15s; }
.team-action:hover, .team-action:focus-visible { border-color: var(--blue); color: var(--blue); background: rgba(0, 117, 174, .07); }
.team-action.danger:hover, .team-action.danger:focus-visible { border-color: var(--risk); color: var(--risk); background: rgba(196, 63, 56, .07); }
/* 「换人」是离职卡片上该做的那件事，常态就用正色 */
.team-action.primary { border-color: var(--blue); color: var(--blue); }
.team-action:disabled { color: var(--muted); border-color: var(--line); background: var(--paper); cursor: not-allowed; }

/* 空岗：虚线框，一眼看出这不是一个人 */
.team-vacancy { display: flex; gap: 10px; padding: 12px 12px 12px 11px; border-style: dashed; border-left: 3px dashed var(--warning-graphic); background: rgba(193, 132, 24, .04); color: inherit; cursor: pointer; }
.team-vacancy.blocked { border-left-color: var(--risk-graphic); background: rgba(196, 63, 56, .04); }
.team-vacancy:disabled { cursor: default; }
.team-vacancy .team-card-body b { padding-right: 0; }
.team-vacancy-note { color: var(--warning); font-size: 12px; }
.team-vacancy-note.blocked { color: var(--risk); font-weight: 700; }
.team-vacancy-cta { color: var(--blue); font-size: 12px; font-weight: 700; }

/* 借人的角色：一排可点的虚线标签 */
.team-borrowed { display: grid; gap: 8px; padding: 12px 14px; border: 1px dashed var(--warning-graphic); border-radius: var(--radius-s); background: rgba(193, 132, 24, .05); }
.team-borrowed-head { display: grid; gap: 2px; }
.team-borrowed-head b { font-size: 13px; }
.team-borrowed-head span { color: var(--muted); font-size: 12px; }
.team-chips { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.team-chip { display: grid; gap: 1px; padding: 5px 10px; border: 1px dashed var(--line-strong); border-radius: 8px; background: var(--white); color: var(--ink); text-align: left; cursor: pointer; transition: border-color .15s, background-color .15s; }
.team-chip b { color: var(--blue); font-size: 12px; }
.team-chip small { color: var(--muted); font-size: 11px; }
.team-chip:hover, .team-chip:focus-visible { border-style: solid; border-color: var(--blue); background: rgba(0, 117, 174, .06); }
.team-chip:disabled { cursor: default; }
.team-position-note { margin: -6px 0 0; color: var(--muted); font-size: 12px; }

/* 组标题旁的「加人」：小而有边框（可点的东西必须有边框） */
.team-add { min-height: 24px; margin-left: 2px; padding: 0 9px; border: 1px dashed var(--line-strong); border-radius: 6px; background: var(--white); color: var(--blue); font: 700 12px var(--body); line-height: 1; cursor: pointer; transition: border-color .15s, background-color .15s; }
.team-add:hover, .team-add:focus-visible { border-style: solid; border-color: var(--blue); background: rgba(0, 117, 174, .07); }

.team-empty { display: grid; gap: 4px; width: 100%; padding: 22px; border: 1px dashed var(--line-strong); border-radius: var(--radius-s); background: var(--paper); text-align: left; cursor: pointer; }
.team-empty:hover, .team-empty:focus-visible { border-color: var(--blue); }
.team-empty.static { cursor: default; }
.team-empty b { font-size: 14px; }
.team-empty span { color: var(--muted); font-size: 12px; line-height: 1.6; }
.team-filter-note { margin: 0; color: var(--muted); font-size: 12px; }
.team-note { margin: 0; padding-top: 10px; border-top: 1px solid var(--line); color: var(--muted); font-size: 12px; line-height: 1.8; }

@media (max-width: 640px) {
  .team-view { padding: 14px; }
  .team-cards { grid-template-columns: 1fr; }
  .team-search input { min-width: 0; width: 100%; }
}
</style>
